import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Plus, Link, Shield, CheckCircle, AlertCircle, Clock,
  RefreshCw, Trash2, X, Copy, ExternalLink, Settings, Loader2,
  Server, Lock, AlertTriangle, HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  verifying: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  verified: 'bg-green-500/20 text-green-400 border-green-500/30',
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  expired: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const SSL_STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-400',
  provisioning: 'bg-blue-500/20 text-blue-400',
  active: 'bg-emerald-500/20 text-emerald-400',
  expired: 'bg-red-500/20 text-red-400',
  failed: 'bg-red-500/20 text-red-400',
};

const CustomDomains = () => {
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState(null);
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [addForm, setAddForm] = useState({ domain: '', funnel_id: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [domainsRes, statsRes, funnelsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/domains`, { headers }),
        axios.get(`${BACKEND_URL}/api/domains/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/funnels`, { headers }),
      ]);

      setDomains(domainsRes.data);
      setStats(statsRes.data);
      setFunnels(funnelsRes.data.funnels || []);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddDomain = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/domains`,
        addForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Domain added. Configure DNS records to verify.');
      setShowAddModal(false);
      setAddForm({ domain: '', funnel_id: '' });
      setSelectedDomain(res.data);
      setShowDetailModal(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifying(domainId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/domains/${domainId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.status === 'active') {
        toast.success('Domain verified and SSL activated!');
      } else {
        toast.error(res.data.message || 'DNS verification failed. Check your records.');
      }
      fetchData();
      
      // Refresh detail modal if open
      if (selectedDomain && selectedDomain.id === domainId) {
        const detailRes = await axios.get(
          `${BACKEND_URL}/api/domains/${domainId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedDomain(detailRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/domains/${domainId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Domain deleted');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete domain');
    }
  };

  const openDomainDetail = async (domainId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/domains/${domainId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedDomain(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load domain details');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div data-testid="custom-domains-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Globe className="w-8 h-8 text-blue-400" />
            Custom Domains
          </h1>
          <p className="text-emerald-300/70">Connect your own domains to funnels with free SSL</p>
        </div>
        <Button
          data-testid="add-domain-btn"
          onClick={() => setShowAddModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Domain
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_domains}</p>
                  <p className="text-xs text-emerald-300/70">Total Domains</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.active_domains}</p>
                  <p className="text-xs text-emerald-300/70">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending_domains}</p>
                  <p className="text-xs text-emerald-300/70">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Shield className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.verified_domains}</p>
                  <p className="text-xs text-emerald-300/70">SSL Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Domains List */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30">
        <CardHeader className="border-b border-emerald-800/30">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-white">Your Domains</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-emerald-400 hover:bg-emerald-900/30"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : domains.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
              <p className="text-emerald-300/50">No custom domains yet</p>
              <p className="text-emerald-300/30 text-sm mt-1">Add your first domain to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-emerald-800/30">
              {domains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between p-4 hover:bg-emerald-900/20 transition-colors cursor-pointer"
                  onClick={() => openDomainDetail(domain.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      domain.status === 'active' ? 'bg-emerald-500/20' :
                      domain.status === 'pending' ? 'bg-amber-500/20' : 'bg-slate-500/20'
                    }`}>
                      <Globe className={`w-5 h-5 ${
                        domain.status === 'active' ? 'text-emerald-400' :
                        domain.status === 'pending' ? 'text-amber-400' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{domain.domain}</p>
                        <Badge className={STATUS_COLORS[domain.status]}>{domain.status}</Badge>
                        {domain.ssl_status === 'active' && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <Lock className="w-3 h-3 mr-1" />
                            SSL
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-emerald-300/70">
                        {domain.funnel_name ? `Connected to: ${domain.funnel_name}` : 'Not connected to any funnel'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-emerald-300/50">
                      Added {formatDate(domain.created_at)}
                    </span>
                    {domain.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerifyDomain(domain.id);
                        }}
                        disabled={verifying === domain.id}
                        className="text-emerald-400 hover:bg-emerald-900/30"
                      >
                        {verifying === domain.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDomain(domain.id);
                      }}
                      className="text-red-400 hover:bg-red-900/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Domain Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Add Custom Domain</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <form onSubmit={handleAddDomain} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Domain *</label>
                  <input
                    type="text"
                    required
                    value={addForm.domain}
                    onChange={(e) => setAddForm({ ...addForm, domain: e.target.value })}
                    placeholder="e.g., landing.yourcompany.com"
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500"
                  />
                  <p className="text-xs text-emerald-300/50 mt-1">
                    Enter your subdomain (e.g., landing.yourcompany.com)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">Connect to Funnel (optional)</label>
                  <select
                    value={addForm.funnel_id}
                    onChange={(e) => setAddForm({ ...addForm, funnel_id: e.target.value })}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="">Select a funnel (optional)</option>
                    {funnels.map((funnel) => (
                      <option key={funnel.id} value={funnel.id}>{funnel.name}</option>
                    ))}
                  </select>
                </div>
                <div className="bg-[#052e22] p-4 rounded-lg border border-emerald-800/30">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-300/70">
                      <p className="font-medium text-emerald-300 mb-1">Next Steps:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Add the domain</li>
                        <li>Configure DNS records at your domain provider</li>
                        <li>Click "Verify" to activate SSL</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Add Domain
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Domain Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedDomain && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedDomain.domain}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={STATUS_COLORS[selectedDomain.status]}>{selectedDomain.status}</Badge>
                    <Badge className={SSL_STATUS_COLORS[selectedDomain.ssl_status]}>
                      <Lock className="w-3 h-3 mr-1" />
                      SSL: {selectedDomain.ssl_status}
                    </Badge>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>

              {/* Connected Funnel */}
              <div className="mb-6 p-4 bg-[#052e22] rounded-lg">
                <p className="text-sm text-emerald-300/70 mb-1">Connected Funnel</p>
                <p className="text-white font-medium">
                  {selectedDomain.funnel_name || 'Not connected to any funnel'}
                </p>
              </div>

              {/* DNS Records Section */}
              {selectedDomain.status !== 'active' && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Server className="w-5 h-5 text-emerald-400" />
                    DNS Configuration Required
                  </h3>
                  <p className="text-sm text-emerald-300/70 mb-4">
                    Add these DNS records at your domain registrar (Cloudflare, GoDaddy, Namecheap, etc.):
                  </p>
                  <div className="space-y-3">
                    {selectedDomain.dns_records?.map((record, index) => (
                      <div key={index} className="bg-[#052e22] p-4 rounded-lg border border-emerald-800/30">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-blue-500/20 text-blue-400">{record.type}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(record.value)}
                            className="text-emerald-400 hover:bg-emerald-900/30"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-emerald-300/50">Name/Host</p>
                            <p className="text-white font-mono">{record.name}</p>
                          </div>
                          <div>
                            <p className="text-emerald-300/50">Value</p>
                            <p className="text-white font-mono text-xs break-all">{record.value}</p>
                          </div>
                        </div>
                        <p className="text-xs text-emerald-300/50 mt-2">{record.purpose}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                      <div className="text-sm text-amber-300">
                        <p className="font-medium">DNS changes can take up to 48 hours to propagate.</p>
                        <p className="text-amber-300/70 mt-1">If using Cloudflare, make sure the proxy is disabled (gray cloud) for verification.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SSL Info */}
              {selectedDomain.status === 'active' && selectedDomain.ssl_expires_at && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-emerald-300 font-medium">SSL Certificate Active</p>
                      <p className="text-sm text-emerald-300/70">
                        Expires: {formatDate(selectedDomain.ssl_expires_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                {selectedDomain.status === 'pending' && (
                  <Button
                    onClick={() => handleVerifyDomain(selectedDomain.id)}
                    disabled={verifying === selectedDomain.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {verifying === selectedDomain.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Verify Domain
                  </Button>
                )}
                {selectedDomain.status === 'active' && (
                  <Button
                    onClick={() => window.open(`https://${selectedDomain.domain}`, '_blank')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit Site
                  </Button>
                )}
                <Button
                  onClick={() => handleDeleteDomain(selectedDomain.id)}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Domain
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDomains;
